import { useMemo } from 'react';

import type { TtqQuestionChainVariantId } from './ttqBehaviorCatalog';
import { TTQ_QUESTION_CHAIN_VARIANTS } from './ttqBehaviorCatalog';
import {
  buildAnchorTextFromSegments,
  buildQuestionExplosion,
  buildVariantPreview,
  variantIdsForSelectedSegments,
  type ExplosionSegment,
} from './ttqQuestionExplosionEngine';

const VARIANT_COLORS: Partial<Record<TtqQuestionChainVariantId, string>> = {
  condition_generalize: 'bg-violet-100 text-violet-800 ring-violet-300',
  condition_specialize: 'bg-sky-100 text-sky-800 ring-sky-300',
  conclusion_progression: 'bg-amber-100 text-amber-900 ring-amber-300',
  inverse_swap: 'bg-orange-100 text-orange-900 ring-orange-300',
  context_transfer: 'bg-emerald-100 text-emerald-900 ring-emerald-300',
  element_weak_strong: 'bg-rose-100 text-rose-900 ring-rose-300',
  question_chain: 'bg-blue-100 text-blue-900 ring-blue-300',
  depth_extend: 'bg-indigo-100 text-indigo-900 ring-indigo-300',
  error_boundary: 'bg-red-100 text-red-900 ring-red-300',
  custom: 'bg-gray-100 text-gray-800 ring-gray-300',
};

function Segment({
  segment,
  selected,
  onToggle,
}: {
  segment: ExplosionSegment;
  selected: boolean;
  onToggle: (seg: ExplosionSegment) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(segment)}
      aria-pressed={selected}
      className={
        'rounded px-0.5 transition-colors hover:bg-yellow-50 '
        + (selected ? 'bg-yellow-100 ring-1 ring-yellow-400' : '')
      }
    >
      {segment.text}
    </button>
  );
}

export type TtqExplosionSelection = {
  segmentIds: string[];
  anchorText?: string;
  variantId: TtqQuestionChainVariantId | null;
};

type TtqQuestionExplosionWorkbenchProps = {
  questionText: string;
  selection: TtqExplosionSelection;
  onSelectionChange: (next: TtqExplosionSelection) => void;
  disabledVariantIds?: string[];
};

export function TtqQuestionExplosionWorkbench({
  questionText,
  selection,
  onSelectionChange,
  disabledVariantIds = [],
}: TtqQuestionExplosionWorkbenchProps) {
  const model = useMemo(() => buildQuestionExplosion(questionText), [questionText]);
  const selectedIds = selection.segmentIds;
  const anchorText = buildAnchorTextFromSegments(model.segments, selectedIds);
  const hasSelection = selectedIds.length > 0;

  const preview = selection.variantId
    ? buildVariantPreview(selection.variantId, selection.anchorText || anchorText)
    : null;

  const toggleSegment = (seg: ExplosionSegment) => {
    const nextIds = selectedIds.includes(seg.id)
      ? selectedIds.filter((id) => id !== seg.id)
      : [...selectedIds, seg.id];
    onSelectionChange({
      segmentIds: nextIds,
      anchorText: buildAnchorTextFromSegments(model.segments, nextIds) || undefined,
      variantId: null,
    });
  };

  const availableVariantIds = variantIdsForSelectedSegments(model.segments, selectedIds);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        点击题干片段可多选，组合完整语义后在下方选择 GO-ON 变式方向
        {hasSelection ? `（已选 ${selectedIds.length} 段）` : ''}
      </p>
      <div className="text-sm text-ink leading-relaxed">
        {model.segments.map((seg) => (
          <Segment
            key={seg.id}
            segment={seg}
            selected={selectedIds.includes(seg.id)}
            onToggle={toggleSegment}
          />
        ))}
      </div>

      {hasSelection || model.unmappedVariants.length > 0 ? (
        <div className="rounded-lg border border-border bg-black/[0.02] p-3 space-y-2">
          <p className="text-xs font-medium text-ink">
            {hasSelection
              ? `已选片段「${(selection.anchorText || anchorText).slice(0, 48)}${(selection.anchorText || anchorText).length > 48 ? '…' : ''}」可选变式`
              : '整题级变式'}
          </p>
          <div className="flex flex-wrap gap-2">
            {(hasSelection
              ? TTQ_QUESTION_CHAIN_VARIANTS.filter((v) => availableVariantIds.includes(v.id))
              : model.unmappedVariants.map((v) => ({ id: v.variantId, label: v.label, description: v.hint }))
            ).map((v) => {
              const disabled = disabledVariantIds.includes(v.id);
              return (
              <button
                key={v.id}
                type="button"
                disabled={disabled}
                className={
                  'rounded-lg border px-2 py-1.5 text-left text-xs transition-colors '
                  + (VARIANT_COLORS[v.id as TtqQuestionChainVariantId] || 'border-border')
                  + (selection.variantId === v.id ? ' ring-2 ring-primary' : '')
                  + (disabled ? ' cursor-not-allowed opacity-50' : '')
                }
                onClick={() => onSelectionChange({
                  segmentIds: selectedIds,
                  anchorText: anchorText || undefined,
                  variantId: v.id as TtqQuestionChainVariantId,
                })}
              >
                <span className="font-medium">{v.label}{disabled ? '（已生成）' : ''}</span>
                {'description' in v && v.description ? (
                  <span className="mt-0.5 block text-muted line-clamp-2">{v.description}</span>
                ) : null}
              </button>
            );
            })}
          </div>
        </div>
      ) : null}

      {preview ? (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-ink whitespace-pre-wrap">
          {preview}
        </div>
      ) : null}
    </div>
  );
}

import type { KpExpansionStrategyId } from '../../features/ttqib/kpExpansionStrategy';

export function mapChainVariantToStrategy(variantId: TtqQuestionChainVariantId): KpExpansionStrategyId {
  return variantId as KpExpansionStrategyId;
}
